/**
 * useLiveKitReconnect
 *
 * `<LiveKitRoom connect>` only calls `room.connect()` from an effect keyed on its
 * own props (connect / token / serverUrl). livekit-client retries internally for
 * ~45s (DefaultReconnectPolicy) and then gives up: it emits `Disconnected` and
 * the Room stays dead. Nothing in @livekit/components-react re-arms it.
 *
 * For a recorder that is fatal and silent. The egress browser keeps capturing the
 * page for the rest of the class, so we get a full-length recording of a frozen
 * frame — exactly what a mid-class `DisconnectReason.CONNECTION_TIMEOUT` produces
 * today.
 *
 * This re-connects the same Room with the same launch token after such a drop,
 * with capped exponential backoff. No token refresh is needed: the class recorder
 * token is minted with a 90d TTL and the ai-tutor recorder token with 6h
 * (lms-backend `generateLiveKitToken` / `generateRecorderToken`), so the token in
 * the page URL stays valid for the whole session.
 *
 * A failed *initial* connect is covered by the same path: livekit-client routes
 * it through Room.handleDisconnect, so it arrives as a `Disconnected` event with
 * a mapped reason (unreachable server -> JOIN_FAILURE, rejected token ->
 * USER_REJECTED). LiveKitRoom itself never retries that either — it only calls
 * `onError`.
 */

import { useEffect, useRef, type FC } from 'react';
import { useRoomContext } from '@livekit/components-react';
import { ConnectionState, DisconnectReason, RoomEvent } from 'livekit-client';
import { datadogLogs } from '@datadog/browser-logs';

/**
 * Backoff schedule; the last entry repeats until MAX_ATTEMPTS is spent. Together
 * these cover ~8 minutes of outage, which comfortably outlives a node drain or a
 * region blip without keeping a hopeless page dialling for the whole class.
 */
const RETRY_DELAYS_MS = [1_000, 2_000, 5_000, 10_000, 20_000, 30_000];
const MAX_ATTEMPTS = 20;

/**
 * A session that stayed up this long counts as a real recovery, so its drop
 * starts a fresh retry budget. Anything shorter is flapping — we keep spending
 * the existing budget so a room that accepts us and immediately kicks us can't
 * loop forever.
 */
const STABLE_CONNECTION_MS = 30_000;

/**
 * Reasons where re-dialling is wrong, not just useless:
 *  - CLIENT_INITIATED  we (or LiveKitRoom's unmount cleanup) asked to leave
 *  - DUPLICATE_IDENTITY a newer recorder took this identity; reconnecting would
 *                       kick it back off and the two would ping-pong
 *  - PARTICIPANT_REMOVED we were kicked
 *  - ROOM_DELETED / ROOM_CLOSED / USER_REJECTED the class is over or we're not welcome
 * Everything else — CONNECTION_TIMEOUT, SIGNAL_CLOSE, SERVER_SHUTDOWN,
 * STATE_MISMATCH, JOIN_FAILURE, MEDIA_FAILURE, UNKNOWN_REASON — is a transport
 * failure worth retrying.
 */
const TERMINAL_REASONS = new Set<DisconnectReason>([
  DisconnectReason.CLIENT_INITIATED,
  DisconnectReason.DUPLICATE_IDENTITY,
  DisconnectReason.PARTICIPANT_REMOVED,
  DisconnectReason.ROOM_DELETED,
  DisconnectReason.ROOM_CLOSED,
  DisconnectReason.USER_REJECTED,
]);

export interface UseLiveKitReconnectArgs {
  /** Same wsUrl the page was launched with. */
  serverUrl: string;
  /** Same LiveKit token the page was launched with. */
  token: string;
  /** Extra fields (classId, page, …) merged into every log line. */
  logContext?: Record<string, unknown>;
  /**
   * Fired once the retry budget is spent — the page is dead for good and
   * whatever depends on room state (e.g. the auto-end watchdog) should stop
   * waiting for it to come back.
   */
  onGiveUp?: () => void;
}

export const useLiveKitReconnect = ({
  serverUrl,
  token,
  logContext,
  onGiveUp,
}: UseLiveKitReconnectArgs): void => {
  const room = useRoomContext();

  const attemptRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const gaveUpRef = useRef(false);
  const terminatedRef = useRef(false);
  const connectedAtRef = useRef<number | null>(null);

  // Held in refs so an inline `onGiveUp` / object literal doesn't tear down and
  // re-subscribe the room listeners on every render.
  const onGiveUpRef = useRef(onGiveUp);
  const logContextRef = useRef(logContext);
  useEffect(() => {
    onGiveUpRef.current = onGiveUp;
    logContextRef.current = logContext;
  });

  useEffect(() => {
    if (!room) return;

    let cancelled = false;
    const baseLog = () => ({
      label: 'livekit-reconnect',
      source: 'recording-app',
      ...logContextRef.current,
    });

    const clearTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const scheduleRetry = () => {
      if (cancelled || terminatedRef.current || timerRef.current) return;

      if (attemptRef.current >= MAX_ATTEMPTS) {
        if (!gaveUpRef.current) {
          gaveUpRef.current = true;
          datadogLogs.logger.error('livekit reconnect exhausted; recorder is offline', {
            ...baseLog(),
            attempts: attemptRef.current,
          });
          onGiveUpRef.current?.();
        }
        return;
      }

      const delayIndex = Math.min(attemptRef.current, RETRY_DELAYS_MS.length - 1);
      // Jitter so a shared failure (region blip, signal restart) doesn't bring
      // every recorder back in the same instant.
      const delayMs = RETRY_DELAYS_MS[delayIndex] + Math.floor(Math.random() * 1_000);
      attemptRef.current += 1;
      const attempt = attemptRef.current;

      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        // The client's own reconnect may have won the race while we waited.
        if (cancelled || room.state !== ConnectionState.Disconnected) return;

        datadogLogs.logger.info('livekit reconnect attempt', {
          ...baseLog(),
          attempt,
          maxAttempts: MAX_ATTEMPTS,
          delayMs,
        });

        // room.connect() is safe to re-issue: it no-ops when already connected
        // and de-dupes concurrent calls through its own connect future.
        room.connect(serverUrl, token).catch((error: unknown) => {
          datadogLogs.logger.warn('livekit reconnect attempt failed', {
            ...baseLog(),
            attempt,
            error: (error as Error)?.message,
          });
          if (!cancelled && room.state === ConnectionState.Disconnected) {
            scheduleRetry();
          }
        });
      }, delayMs);
    };

    const handleDisconnected = (reason?: DisconnectReason) => {
      const wasStable =
        connectedAtRef.current !== null &&
        Date.now() - connectedAtRef.current >= STABLE_CONNECTION_MS;
      connectedAtRef.current = null;

      // A drop after a healthy session earns a fresh budget; a flapping one
      // keeps spending the current budget so it eventually gives up.
      if (wasStable) {
        attemptRef.current = 0;
        gaveUpRef.current = false;
      }

      if (reason !== undefined && TERMINAL_REASONS.has(reason)) {
        datadogLogs.logger.info('livekit disconnected; not retrying', {
          ...baseLog(),
          reason: DisconnectReason[reason],
        });
        // Latched, not just "return": a failing room.connect() reports the same
        // drop twice — once as this event, then again as the rejected promise —
        // and the rejection path must not re-arm what we just declared final.
        terminatedRef.current = true;
        clearTimer();
        return;
      }

      datadogLogs.logger.warn('livekit disconnected; scheduling reconnect', {
        ...baseLog(),
        reason: reason !== undefined ? DisconnectReason[reason] : 'undefined',
      });
      scheduleRetry();
    };

    const handleConnected = () => {
      clearTimer();
      terminatedRef.current = false;
      connectedAtRef.current = Date.now();
      if (attemptRef.current > 0) {
        datadogLogs.logger.info('livekit reconnected', {
          ...baseLog(),
          attempts: attemptRef.current,
        });
      }
    };

    // Already up when we mounted (StrictMode re-run, or a remounted child).
    if (room.state === ConnectionState.Connected) {
      connectedAtRef.current = Date.now();
    }

    room.on(RoomEvent.Disconnected, handleDisconnected);
    room.on(RoomEvent.Connected, handleConnected);

    return () => {
      cancelled = true;
      clearTimer();
      room.off(RoomEvent.Disconnected, handleDisconnected);
      room.off(RoomEvent.Connected, handleConnected);
    };
  }, [room, serverUrl, token]);
};

/**
 * Headless controller — render inside <LiveKitRoom> to keep the recorder dialling
 * back after a dropped connection. Renders nothing.
 */
const LiveKitReconnect: FC<UseLiveKitReconnectArgs> = (props) => {
  useLiveKitReconnect(props);
  return null;
};

export default LiveKitReconnect;
